import AsyncSelect from 'react-select/async';
import classNames from 'classnames';
import tzLookup from 'tz-lookup';

import { searchCities } from '../../api/geocoding';
import styles from '../FormInput/FormInput.module.scss';

export interface CityOption {
  label: string;
  value: string;
  lat: number;
  lon: number;
}

interface CitySelectProps {
  id: string;
  label: string;
  value?: CityOption | null;
  onChange: (city: CityOption | null, timezone?: string) => void;
  placeholder?: string;
  required?: boolean;
  error?: string | null;
  lang?: string;
}

export function CitySelect({
  id,
  label,
  value,
  onChange,
  placeholder = 'Введите город',
  required = false,
  error = null,
  lang = 'ru',
}: CitySelectProps) {
  const loadOptions = async (input: string): Promise<CityOption[]> => {
    const data = await searchCities(input, lang);
    return data.map(item => ({
      label: item.display_name,
      value: item.display_name,
      lat: parseFloat(item.lat),
      lon: parseFloat(item.lon),
    }));
  };

  return (
    <div className={classNames(styles.formInput, { [styles.formInputError]: error })}>
      <label htmlFor={id} className={styles.label}>
        {label}
        {required && <span className={styles.required}>*</span>}
      </label>

      <AsyncSelect<CityOption, false>
        inputId={id}
        cacheOptions
        defaultOptions
        loadOptions={loadOptions}
        placeholder={placeholder}
        value={value || null}
        onChange={opt => {
          if (opt) {
            const timezone = tzLookup(opt.lat, opt.lon);
            onChange(opt, timezone);
          } else {
            onChange(null);
          }
        }}
        noOptionsMessage={() => 'Город не найден'}
        isClearable
        styles={{
          control: (base, state) => ({
            ...base,
            padding: '0',
            minHeight: '44px',
            border: state.isFocused
              ? '2px solid var(--main-color-light)'
              : `1px solid ${error ? 'var(--error-color)' : '#807B76'}`,
            borderRadius: '4px',
            backgroundColor: 'var(--main-color-light)',
            transition: 'border-color 0.2s ease',
            boxShadow: 'none',
            '&:hover': {
              borderColor: state.isFocused ? 'var(--main-color-light)' : '#807B76',
            },
          }),
          valueContainer: base => ({
            ...base,
            padding: '0 10px',
            fontSize: '14px',
            fontFamily: 'var(--font-family), sans-serif',
            fontWeight: 300,
            color: 'var(--bg-fill-color)',
            flexWrap: 'nowrap',
          }),
          singleValue: base => ({
            ...base,
            color: 'var(--bg-fill-color)',
            fontSize: '14px',
            fontFamily: 'var(--font-family), sans-serif',
            fontWeight: 300,
            whiteSpace: 'normal',
            wordWrap: 'break-word',
            lineHeight: 1.4,
          }),
          placeholder: base => ({
            ...base,
            color: '#807B76',
            fontSize: '14px',
            fontFamily: 'var(--font-family), sans-serif',
            fontWeight: 300,
          }),
          menu: base => ({
            ...base,
            zIndex: 2000,
            borderRadius: '4px',
            marginTop: '4px',
            backgroundColor: 'var(--main-color-light)',
            maxWidth: '100%',
          }),
          option: (base, state) => ({
            ...base,
            padding: '10px',
            cursor: 'pointer',
            fontSize: '14px',
            fontFamily: 'var(--font-family), sans-serif',
            fontWeight: 300,
            whiteSpace: 'normal',
            wordWrap: 'break-word',
            lineHeight: 1.4,
            backgroundColor: state.isSelected
              ? 'var(--main-color-medium)'
              : state.isFocused
                ? 'var(--main-color)'
                : 'var(--main-color-light)',
            color: state.isSelected ? 'var(--bg-fill-color)' : '#392F1F',
          }),
        }}
      />

      {error && <p className={styles.error}>{error}</p>}
    </div>
  );
}
